'use client';

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback
} from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGamepad } from '@fortawesome/free-solid-svg-icons';
import { ACCOUNT_EXPIRED_MESSAGE, AUTH_NOTICE_STORAGE_KEY } from '@/lib/accountExpiry';

interface User {
  id: number;
  name: string;
  surname: string;
  username: string;
  email: string;
  company: string | null;
  company_id: number | null;
  role: string | null;
  created_at: string;
  avatar_url?: string;
  /** The super admin acting as this user, if any (WORKFLOW_ACT_AS_USER.md). */
  act_as_by?: number | null;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (
    username: string,
    password: string
  ) => Promise<{ success: boolean; error?: string; code?: string }>;
  logout: () => void;
  loading: boolean;
  completeLogin: (user: User) => void;
  switchCompany: (companyId: number) => Promise<{ success: boolean; error?: string }>;
  /** Super admin only: become `userId` for this session (server re-signs the cookie). */
  actAs: (userId: number) => Promise<{ success: boolean; error?: string }>;
  /** Return from `actAs` to the admin's own session. */
  stopActingAs: () => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Guard for redirect targets that came from the URL or sessionStorage.
 *
 * Only same-origin absolute paths are honoured. A leading '/' on its own is NOT
 * enough: '//evil.com' is a protocol-relative URL the browser resolves to a
 * different origin, so it would be an open redirect. Absolute URLs
 * ('https://evil.com') fail the first check.
 *
 * Deliberately duplicated from src/proxy.ts rather than shared — proxy.ts is
 * Edge middleware and importing it into a client bundle drags the runtime with it.
 */
function isSafeReturnUrl(value: string | null): value is string {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return false;
  // Bouncing back to the pages that only exist for logged-OUT users would loop.
  const path = value.split('?')[0];
  return path !== '/login' && path !== '/signup';
}

/**
 * Where to land after a successful login. Priority:
 *
 * 1. `?returnUrl=` on the current URL — stamped server-side by proxy.ts. This is
 *    the only carrier that survives a cold click on a deep link (e.g. the
 *    `/slack/link?code=…` URL Slack hands out), because the 307 happens before
 *    any client component mounts.
 * 2. sessionStorage `returnUrl` — covers client-side navigations, where
 *    AuthProvider itself performed the redirect.
 * 3. `/workflow`, the default landing that routes on by workflow_role.
 */
function resolvePostLoginRedirect(): string {
  if (typeof window === 'undefined') return '/workflow';

  const fromQuery = new URLSearchParams(window.location.search).get('returnUrl');
  if (isSafeReturnUrl(fromQuery)) {
    sessionStorage.removeItem('returnUrl');
    return fromQuery;
  }

  const stored = sessionStorage.getItem('returnUrl');
  if (isSafeReturnUrl(stored)) {
    sessionStorage.removeItem('returnUrl');
    return stored;
  }

  return '/workflow';
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const router = useRouter();
  const pathname = usePathname();
  // Set for the duration of an explicit logout(), so the "unauthenticated on a
  // protected path" effect below does not stash the page we are LEAVING as
  // `returnUrl` — router.push('/login') from logout() does not resolve to the
  // new pathname synchronously, so this effect can otherwise still see the old
  // pathname on the render where isAuthenticated flips to false, save it as
  // returnUrl, and hand it to whichever (possibly different) user logs in next.
  const isLoggingOutRef = useRef(false);
  // Pathname the session was last verified on. The mount check stamps it so the
  // route-change re-check below does not fire a second, redundant request for
  // the page we loaded on.
  const verifiedPathRef = useRef<string | null>(null);

  useEffect(() => {
    // Check authentication status via server-side session cookie
    const checkSession = async () => {
      verifiedPathRef.current = pathname;
      try {
        const response = await fetch('/api/auth/session');
        const data = await response.json();

        if (data.authenticated && data.user) {
          setIsAuthenticated(true);
          setUser(data.user);
          // Cache user data in localStorage for component reads
          if (typeof window !== 'undefined') {
            localStorage.setItem('user', JSON.stringify(data.user));
          }
        } else {
          setIsAuthenticated(false);
          setUser(null);
          if (typeof window !== 'undefined') {
            localStorage.removeItem('user');
            // The server signed this account out because its access end date
            // arrived. Hand the explanation to the login page, which renders it
            // as a persistent notice (docs/designs/account-expiry-dates.md).
            if (data.reason === 'account_expired') {
              sessionStorage.setItem(AUTH_NOTICE_STORAGE_KEY, ACCOUNT_EXPIRED_MESSAGE);
            }
          }
        }
      } catch (error) {
        console.error('Session check error:', error);
        setIsAuthenticated(false);
        setUser(null);
      }

      setIsLoading(false);
    };

    checkSession();
    // Mount-only: the route-change re-check below handles later navigations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-verify the session on every client-side navigation while signed in. The
  // cookie lives 15 days, but an admin can end an account's access at any time
  // (docs/designs/account-expiry-dates.md): /api/auth/session re-reads the end
  // date, clears the cookie and answers `reason: 'account_expired'` once it has
  // arrived, so the user is signed out within one click instead of at their
  // next full page load. Only the sign-out case acts here — a transient network
  // failure must not log anyone out, and the loading spinner is not shown.
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    if (verifiedPathRef.current === pathname) return;
    verifiedPathRef.current = pathname;

    let cancelled = false;
    const revalidate = async () => {
      try {
        const response = await fetch('/api/auth/session');
        const data = await response.json();
        if (cancelled || data.authenticated) return;

        setIsAuthenticated(false);
        setUser(null);
        if (typeof window !== 'undefined') {
          localStorage.removeItem('user');
          if (data.reason === 'account_expired') {
            sessionStorage.setItem(AUTH_NOTICE_STORAGE_KEY, ACCOUNT_EXPIRED_MESSAGE);
          }
        }
      } catch (error) {
        console.error('Session re-check error:', error);
      }
    };
    revalidate();

    return () => {
      cancelled = true;
    };
  }, [pathname, isAuthenticated, isLoading]);

  useEffect(() => {
    if (!isLoading) {
      const unauthenticatedOnlyPaths = ['/login', '/signup'];

      if (!isAuthenticated && !unauthenticatedOnlyPaths.includes(pathname)) {
        // Store the intended destination before redirecting to login — path AND
        // query string. Dropping the query breaks any deep link whose payload
        // lives there, e.g. /slack/link?code=… arrived at from Slack.
        // Skip this during an explicit logout: router.push('/login') there has
        // not resolved yet, so this effect can still see the page being LEFT —
        // saving it would hand it to whichever user logs in next, regardless of
        // role (see isLoggingOutRef's declaration above).
        if (typeof window !== 'undefined' && !isLoggingOutRef.current) {
          sessionStorage.setItem('returnUrl', `${pathname}${window.location.search}`);
        }
        router.push('/login');
      } else if (
        isAuthenticated &&
        (unauthenticatedOnlyPaths.includes(pathname) || pathname === '/')
      ) {
        isLoggingOutRef.current = false;
        // Default landing is the workflow entry, which routes on by workflow_role
        // (customer → requests, QA → qa, …); nobody lands on the old dashboard.
        // A returnUrl takes precedence.
        router.push(resolvePostLoginRedirect());
      }
    }
  }, [isAuthenticated, isLoading, pathname, router]);

  const login = async (
    username: string,
    password: string
  ): Promise<{ success: boolean; error?: string; code?: string }> => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Cookie is set server-side; cache user in localStorage for component reads
        if (typeof window !== 'undefined') {
          localStorage.setItem('user', JSON.stringify(data.user));
        }
        setIsAuthenticated(true);
        setUser(data.user);
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Login failed', code: data.code };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const logout = async () => {
    isLoggingOutRef.current = true;
    try {
      // Clear the HTTP-only cookie via server
      await fetch('/api/auth/logout', { method: 'POST' });
    } catch (error) {
      console.error('Logout API error:', error);
    }

    if (typeof window !== 'undefined') {
      localStorage.removeItem('user');
      localStorage.removeItem('google_access_token');
      localStorage.removeItem('google_tokens');
      // A leftover returnUrl from an earlier redirect-to-login would otherwise
      // survive an explicit sign-out and get handed to the NEXT login — which
      // may be a different user with a different role landing on a page that
      // was never routed for them.
      sessionStorage.removeItem('returnUrl');
    }
    setIsAuthenticated(false);
    setUser(null);
    router.push('/login');
  };

  const completeLogin = (user: User) => {
    // Cookie is already set by the auth API route (google/login)
    if (typeof window !== 'undefined') {
      localStorage.setItem('user', JSON.stringify(user));
    }
    setIsAuthenticated(true);
    setUser(user);

    // Everyone lands on the workflow entry, which routes on by workflow_role
    // (customer → requests, QA → qa, …). Internal users reach the editor via the
    // "Back to Dashboard" button. A returnUrl takes precedence — including the
    // one proxy.ts put on the URL, so the Google path honours deep links too.
    router.push(resolvePostLoginRedirect());
  };

  const switchCompany = async (
    companyId: number
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      if (!user) {
        return { success: false, error: 'No user logged in' };
      }

      if (user.role !== 'admin') {
        return { success: false, error: 'Only admins can switch companies' };
      }

      const response = await fetch('/api/auth/switch-company', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ companyId })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Cookie is re-issued server-side; update localStorage cache
        if (typeof window !== 'undefined') {
          localStorage.setItem('user', JSON.stringify(data.user));
        }
        setUser(data.user);
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Failed to switch company' };
      }
    } catch (error) {
      console.error('Switch company error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  // The cookie was re-issued server-side; mirror the returned user locally.
  const adoptSessionUser = (nextUser: User) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('user', JSON.stringify(nextUser));
    }
    setUser(nextUser);
  };

  const actAs = async (userId: number): Promise<{ success: boolean; error?: string }> => {
    try {
      if (!user) {
        return { success: false, error: 'No user logged in' };
      }

      const response = await fetch('/api/auth/act-as', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        adoptSessionUser(data.user);
        return { success: true };
      }
      return { success: false, error: data.error || 'Failed to act as this user' };
    } catch (error) {
      console.error('Act as error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const stopActingAs = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch('/api/auth/act-as', { method: 'DELETE' });
      const data = await response.json();

      if (response.ok && data.success) {
        adoptSessionUser(data.user);
        return { success: true };
      }
      return { success: false, error: data.error || 'Failed to stop acting as this user' };
    } catch (error) {
      console.error('Stop acting as error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const loginAction = useCallback(login, []);
  const logoutAction = useCallback(logout, [router]);
  const completeLoginAction = useCallback(completeLogin, [router]);
  const switchCompanyAction = useCallback(switchCompany, [user]);
  const actAsAction = useCallback(actAs, [user]);
  const stopActingAsAction = useCallback(stopActingAs, []);

  const contextValue = useMemo(
    () => ({
      isAuthenticated,
      user,
      login: loginAction,
      logout: logoutAction,
      loading: isLoading,
      completeLogin: completeLoginAction,
      switchCompany: switchCompanyAction,
      actAs: actAsAction,
      stopActingAs: stopActingAsAction
    }),
    [
      isAuthenticated,
      user,
      loginAction,
      logoutAction,
      isLoading,
      completeLoginAction,
      switchCompanyAction,
      actAsAction,
      stopActingAsAction
    ]
  );

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'var(--bg-primary)' }}
      >
        <div className="text-center">
          <div className="animate-spin text-4xl mb-4">
            <FontAwesomeIcon icon={faGamepad} />
          </div>
          <p style={{ color: 'var(--text-secondary)' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * The session when there is one, `undefined` when this subtree has no
 * AuthProvider above it — for leaf components that only IMPROVE with a session
 * (a sensible default company, say) rather than depending on one. Throwing
 * there would make the component untestable in isolation and would couple every
 * consumer to the provider for a fallback they may never reach.
 */
export function useOptionalAuth() {
  return useContext(AuthContext);
}
